import { CustomerAccount } from '@team-milva/lab-models/customerAccount'
import { InsuranceCustomerAccount } from '@team-milva/lab-models/insuranceCustomerAccount'
import type { ICustomerRef } from '@team-milva/lab-models/customer'

/**
 * Where a one-off Bilag for a single order is uploaded.
 *
 * Under the customer's own prefix, built by the model (`orderBilagUploadPath`) — the Storage
 * rules (MIL-325) only let a lab user write order Bilag there. The old
 * `labAccounts/{lab}/orders/{orderID}/bilag/…` is no longer client-writable, and an upload to it
 * surfaces only as an opaque `storage/unauthorized`.
 *
 * A private-segment order has no customer account (`customerAccountID` is blank), and an empty
 * segment would match no rule either, so it falls back to the lab's own prefix — the same
 * fallback `customerScopedStoragePath` gives the server. No rule opens that path to clients
 * today, so an upload there is still denied; it is the honest location, not a working one.
 *
 * An insurance customer's own order (MIL-402, `customer.type === 'insurance'`) has no customer
 * account either; its Bilag go under the insurance customer's prefix, which the rules open to the
 * lab the same way.
 */
export function getOrderBilagUploadPath({
  labAccountID,
  customerAccountID,
  customer,
  orderID,
  attachmentID,
}: {
  labAccountID: string
  customerAccountID: string | null | undefined
  /** The order's owner. Only an insurance owner changes the result. */
  customer?: ICustomerRef | null
  orderID: string
  attachmentID: string
}): string {
  const folder =
    customer?.type === 'insurance' && customer.id
      ? new InsuranceCustomerAccount({ id: customer.id, labAccountID }).orderBilagUploadPath(orderID)
      : customerAccountID
    ? new CustomerAccount({
        id: customerAccountID,
        labAccountID,
      }).orderBilagUploadPath(orderID)
    : `labAccounts/${labAccountID}/orders/${orderID}/bilag`

  return `${folder}/${attachmentID}.pdf`
}
